import { individualTaxIdTypesForCountry, taxIdCountries } from '@hub-wifi/shared'
import type { TaxIdType } from '@hub-wifi/shared'

/** Countries that offer at least one individual (natural-person) tax-id
 * type — enrollment only ever accepts individuals (Brazil: CPF), never
 * company/VAT/employer types. Data-driven off `individualTaxIdTypesForCountry`
 * so this adapts automatically if more individual types/countries are added
 * upstream in `@hub-wifi/shared`. Not exported — pages that need the raw
 * country list for a `taxIdCountry` default derive it themselves; this
 * module only exports the pieces that were duplicated verbatim across
 * EnrollPage/MyEnrollPage. */
const INDIVIDUAL_TAX_ID_COUNTRIES = taxIdCountries().filter(
  (country) => individualTaxIdTypesForCountry(country).length > 0,
)

export const ALL_INDIVIDUAL_TAX_ID_TYPES: TaxIdType[] = INDIVIDUAL_TAX_ID_COUNTRIES.flatMap(
  (country) => individualTaxIdTypesForCountry(country),
)

/** Passport is a second individual tax-id type (added alongside BR/CPF), but
 * it is surfaced via the inline "I don't have a CPF" toggle, not via the
 * generic taxIdCountry/taxIdType Selects — so it is EXCLUDED from the "how
 * many individual types" count that governs whether those Selects reappear
 * (regression guard for PR #161, which collapsed them away when exactly one
 * individual type existed). Without this exclusion, adding `passport`
 * upstream would make `ALL_INDIVIDUAL_TAX_ID_TYPES.length` become 2 and
 * incorrectly bring the Selects back in both CPF and passport mode. */
export const TOGGLEABLE_TAX_ID_TYPE_ID = 'passport'

export const SELECTABLE_INDIVIDUAL_TAX_ID_TYPES = ALL_INDIVIDUAL_TAX_ID_TYPES.filter(
  (type) => type.id !== TOGGLEABLE_TAX_ID_TYPE_ID,
)

/** When exactly one *selectable* individual tax-id type exists (today's
 * reality: BR/CPF — passport is excluded, see above), the field collapses
 * the taxIdCountry/taxIdType selects away and renders just the value input,
 * labeled by the type, with the passport toggle underneath. If a second
 * selectable individual type or country is ever added upstream, this
 * becomes `undefined` and the (dead-but-correct) Selects branch renders
 * again. */
export const SINGLE_INDIVIDUAL_TAX_ID_TYPE: TaxIdType | undefined =
  SELECTABLE_INDIVIDUAL_TAX_ID_TYPES.length === 1
    ? SELECTABLE_INDIVIDUAL_TAX_ID_TYPES[0]
    : undefined

export const PASSPORT_TAX_ID_TYPE: TaxIdType | undefined = ALL_INDIVIDUAL_TAX_ID_TYPES.find(
  (type) => type.id === TOGGLEABLE_TAX_ID_TYPE_ID,
)

/** Applies a `mask` metadata pattern (e.g. `'000.000.000-00'`, `0` = digit
 * placeholder) to a raw string's digits, for live-as-you-type formatting.
 * Display-only — normalization for the wire still goes through
 * `normalizeTaxId` at submit time, exactly as before this mask existed. */
export function applyDigitMask(value: string, mask: string): string {
  const digits = value.replace(/\D/g, '')
  let out = ''
  let digitIndex = 0
  for (let i = 0; i < mask.length && digitIndex < digits.length; i++) {
    if (mask[i] === '0') {
      out += digits[digitIndex]
      digitIndex++
    } else {
      out += mask[i]
    }
  }
  return out
}
